import { MemberStatus, Role, User } from '@prisma/client';
import { ColumnDef, FilterFn } from '@tanstack/react-table';
import { ListMembersResponse } from '@zond/api-interfaces';
import { Badge, Checkbox } from '@zond/ui';
import { cn } from '@zond/utils';
import LongText from '../../../../components/long-text';
import i18n from '../../../../i18n/config';
import { callTypes, userTypes } from '../data/data';
import { DataTableColumnHeader } from './data-table-column-header';
import { DataTableRowActions } from './data-table-row-actions';

const fuzzyFilter: FilterFn<ListMembersResponse[0]> = (row, columnId, value) => {
  const searchValue = value.toLowerCase();
  const cellValue = row.getValue(columnId);
  if (typeof cellValue === 'string') {
    return cellValue.toLowerCase().includes(searchValue);
  }
  return false;
};

const multiFilter: FilterFn<ListMembersResponse[0]> = (row, columnId, value) => {
  const values = value as unknown[];
  const cellValue = row.getValue(columnId);
  if (Array.isArray(cellValue)) {
    return cellValue.some((val) => values.includes(val));
  }
  return values.includes(cellValue);
};

export const columns: ColumnDef<ListMembersResponse[0]>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={i18n.t('settings:members.table.selectAll')}
        className="translate-y-[2px]"
      />
    ),
    meta: {
      className: cn(
        'sticky md:table-cell left-0 z-10 rounded-tl',
        'bg-background transition-colors duration-200 group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted'
      ),
    },
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={i18n.t('settings:members.table.selectRow')}
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: 'name',
    accessorFn: (row) => row.user?.displayname || row.email,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('settings:members.table.columns.name')} />
    ),
    cell: ({ row }) => {
      const displayname = row.original.user?.displayname;
      const isInvited = row.original.status === MemberStatus.Invited;

      return (
        <div className="max-w-36 truncate">
          {isInvited ? (
            <span className="text-muted-foreground">{i18n.t('settings:members.table.pending')}</span>
          ) : (
            displayname || '—'
          )}
        </div>
      );
    },
  },
  {
    id: 'email',
    accessorFn: (row) => row.user?.email || row.email,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('settings:members.table.columns.email')} />
    ),
    cell: ({ row }) => {
      const email = row.original.user?.email || row.original.email;
      return (
        <div className="flex items-center">
          <div className="max-w-[200px] truncate font-medium">{email || '—'}</div>
        </div>
      );
    },
  },
  {
    id: 'username',
    accessorFn: (row) => row.user?.displayname || row.email,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('settings:members.table.columns.search')} />
    ),
    filterFn: fuzzyFilter,
    enableHiding: true,
  },
  {
    id: 'status',
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('settings:members.table.columns.status')} />
    ),
    cell: ({ row }) => {
      const status = row.getValue('status') as MemberStatus;
      const badgeColor = callTypes.get(status);
      return (
        <div className="flex space-x-2">
          <Badge variant="outline" className={cn('capitalize', badgeColor)}>
            {status.toLowerCase()}
          </Badge>
        </div>
      );
    },
    filterFn: multiFilter,
    enableHiding: false,
  },
  {
    id: 'role',
    accessorFn: (row) => row.roles,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={i18n.t('settings:members.table.columns.role')} />
    ),
    cell: ({ row }) => {
      const roles = row.getValue('role') as Role[];
      const primaryRole = roles[0];
      const userType = userTypes.find(({ value }) => value === primaryRole);

      if (!userType) return null;

      return (
        <div className="flex items-center gap-x-2">
          {userType.icon && <userType.icon className="h-4 w-4 text-muted-foreground" />}
          <span className="text-sm capitalize">{primaryRole.toLowerCase()}</span>
        </div>
      );
    },
    filterFn: multiFilter,
    enableHiding: false,
  },
  {
    id: 'actions',
    cell: ({ row }) => <DataTableRowActions row={row} />,
  },
];
